import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IPhoneCallTag } from 'voxloud-types/dist/models';
import { TranslateService } from '@ngx-translate/core';
import { CompanyCallHistoryService } from '../../services/company-call-history/company-call-history.service';
import { IPhoneCallTagUI, TagError } from '../../models/company-call-history/company-call-history.model';
import { TagInputComponent } from './components/tag-input/tag-input.component';

@Component({
  selector: 'company-call-history-tags-managment',
  templateUrl: './company-call-history-tags-managment.component.html',
  styleUrls: ['./company-call-history-tags-managment.component.scss']
})
export class CompanyCallHistoryTagsManagmentComponent {
  @ViewChild('newTagInput') newTagInput!: TagInputComponent;

  @Input() set tags(tags: IPhoneCallTag[]) {
    this._allTags = (tags || []).map(tag => ({
      ...tag,
      isEditing: this.editingTagId != null && tag.id === this.editingTagId
    }));
  }

  @Output() refreshTags: EventEmitter<void> = new EventEmitter();

  readonly MAX_TAG_LENGTH = 32;
  public _allTags: IPhoneCallTagUI[] = [];
  public isEditMode: boolean = false;
  public isUpdating: boolean = false;
  public savingTagId: number | null = null;
  public addError: boolean = false;
  public editError: boolean = false;

  public newTagValue: string = '';
  public editTagValue: string = '';
  private editingTagId: number | null = null;

  constructor(
    private companyCallHistoryService: CompanyCallHistoryService,
    private translateService: TranslateService
  ) {}

  public onEditEnable(tag: IPhoneCallTagUI) {
    tag.isEditing = true;
    this.isEditMode = true;
    this.editTagValue = tag.value;
    this.editingTagId = tag.id;
  }

  public addEditTag(tagId?: number | null) {
    const hasError = tagId != null ? !!this.editTagError : !!this.newTagError;
    if (hasError) return;
    if (tagId != null ? this.savingTagId === tagId : this.isUpdating) return;
    if (tagId != null) {
      const original = this._allTags.find(t => t.id === tagId);
      if (original && original.value.trim() === this.editTagValue.trim()) {
        this.onCancel(original);
        return;
      }
    }

    const originalTag = this._allTags.find(t => t.id === tagId);
    const tag: IPhoneCallTag = tagId != null
      ? { id: originalTag?.id, companyId: originalTag?.companyId, value: this.editTagValue.trim() }
      : { id: null, companyId: null, value: this.newTagValue.trim() };

    if (tagId != null) {
      this.savingTagId = tagId;
      this.editError = false;
    } else {
      this.isUpdating = true;
      this.addError = false;
    }

    this.companyCallHistoryService.updateTag(tag).subscribe({
      next: (response: IPhoneCallTag) => {
        const tagUI: IPhoneCallTagUI = { ...response, isEditing: false };
        if (tagId == null) {
          this._allTags.push(tagUI);
          if (this.newTagValue.trim() === tag.value) {
            this.newTagValue = '';
            this.newTagInput.resetState();
          }
          this.isUpdating = false;
          this.addError = false;
        } else {
          const idx = this._allTags.findIndex(t => t.id === response.id);
          if (idx > -1) {
            this._allTags[idx] = tagUI;
          }
          this.savingTagId = null;
          if (this.editTagValue.trim() === tag.value) {
            this.isEditMode = false;
            this.editingTagId = null;
          }
          this.editError = false;
        }
        this.refreshTags.emit();
      },
      error: () => {
        if (tagId != null) {
          this.savingTagId = null;
          this.editError = true;
        } else {
          this.isUpdating = false;
          this.addError = true;
        }
      }
    });
  }

  public onCancel(tag: IPhoneCallTagUI) {
    tag.isEditing = false;
    this.isEditMode = false;
    this.editingTagId = null;
    this.editError = false;
  }

  private validateTag(tagValue: string, excludeId?: number | null): TagError | null {
    const v = (tagValue ?? '').trim();
    if (!v) return 'required';
    if (v.length > this.MAX_TAG_LENGTH) return 'maxlength';
    if (this.isDuplicate(v, excludeId)) return 'exists';
    return null;
  }

  private isDuplicate(tagValue: string, excludeId?: number | null): boolean {
    return !!this._allTags.find(tag => tag.value.trim() === tagValue && tag.id !== excludeId);
  }

  public get newTagError(): TagError | null {
    return this.validateTag(this.newTagValue);
  }

  public get editTagError(): TagError | null {
    return this.validateTag(this.editTagValue, this.editingTagId);
  }

  public dismissError(): void {
    this.addError = false;
    this.editError = false;
  }

  public get apiErrorMessage(): string | null {
    const prefix = 'events.company-call-history.events-all.general-settings.accordion.tags.error.';
    if (this.addError) {
      return this.translateService.instant(prefix + 'add');
    }
    if (this.editError) {
      return this.translateService.instant(prefix + 'edit', { tag: this.editTagValue });
    }
    return null;
  }
}